/**
 * Vector ornaments (ui-ux §20.2; art §9.2 "kept as SVG data URIs"): penwork edges, corner flourishes, arcade cells,
 * lozenge, deckle, quill stroke, scroll ends, manicule, the 16-lobe wax mask, drop-cap penwork, the pearl row, tile
 * diaper and the tile state stamps. Set as CSS custom properties at boot by {@link applyOrnaments}. Owner: UI/Visual (F3).
 *
 * Colours are art §2.1 pigments only. Everything here is crisp at DPR 3 and recolours for «Ночной список» (P1).
 */
import { TOKENS as P } from '../content/palette';

const enc = (svg: string): string => `url("data:image/svg+xml,${svg.replace(/\s{2,}/g, ' ').replace(/"/g, "'")
  .replace(/[<>#%{}|\\^`]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`)}")`;
const svg = (w: number, h: number, body: string, extra = ''): string =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" ${extra}>${body}</svg>`;

// ── penwork (rubricator's filigree) ────────────────────────────────────────────────────────────────────────────────

/** 12 px tall, 24 px tileable penwork strip: a rule, a fringe of hair strokes, scroll buds and pearls. */
function penworkEdge(c: string): string {
  const hair = Array.from({ length: 8 }, (_, i) => `M${1.5 + i * 3} 10V${i % 2 ? 7.6 : 8.4}`).join('');
  return svg(24, 12, `
    <path d="M0 10.5H24" stroke="${c}" stroke-width="1.1"/>
    <path d="${hair}" stroke="${c}" stroke-width=".55" stroke-linecap="round"/>
    <path d="M12 10.2C12 7 9.6 5 7.4 5.6C5.6 6.1 5.8 8.4 7.6 8.3M12 10.2C12 7 14.4 5 16.6 5.6C18.4 6.1 18.2 8.4 16.4 8.3"
      fill="none" stroke="${c}" stroke-width=".8" stroke-linecap="round"/>
    <circle cx="12" cy="3.1" r="1.25" fill="${c}"/><circle cx="0" cy="6.6" r=".95" fill="${c}"/><circle cx="24" cy="6.6" r=".95" fill="${c}"/>
    <path d="M12 4.6V6.4" stroke="${c}" stroke-width=".6"/>`, 'preserveAspectRatio="none"');
}

/** 24 px pen-flourish corner (top-left orientation; CSS mirrors it for the other corners). */
function corner(c: string, c2: string): string {
  return svg(24, 24, `
    <path d="M2.2 23V7.5C2.2 4.5 4.5 2.2 7.5 2.2H23" fill="none" stroke="${c}" stroke-width="1.2"/>
    <path d="M6 20C6 12 9.5 7.2 15 6.2C18 5.7 19.6 7.6 18.4 9.4C17.4 10.9 15.2 10.4 15.4 8.9" fill="none" stroke="${c}" stroke-width=".85" stroke-linecap="round"/>
    <path d="M5.2 5.2l2.6 2.6" stroke="${c2}" stroke-width="1.6" stroke-linecap="round"/>
    <circle cx="5" cy="5" r="1.6" fill="${c}"/><circle cx="21.5" cy="5.6" r=".9" fill="${c}"/><circle cx="5.6" cy="21.5" r=".9" fill="${c}"/>
    <path d="M9.5 4.6V2.2M12.5 4.6V2.2M15.5 4.6V2.2M4.6 9.5H2.2M4.6 12.5H2.2M4.6 15.5H2.2" stroke="${c}" stroke-width=".5"/>`);
}

/** Drop-cap penwork: filigree inside the initial's box (the letter sits on top). */
function dropcapPenwork(c: string): string {
  const curl = (x: number, y: number, sx: number, sy: number): string =>
    `<path transform="translate(${x} ${y}) scale(${sx} ${sy})" d="M0 0C0 8 3 13 9 14C13 14.6 15 12 13.6 10C12.4 8.4 10 9 10.2 10.8" fill="none" stroke="${c}" stroke-width="1.1" stroke-linecap="round"/>`;
  return svg(64, 64, `
    <rect x="3.5" y="3.5" width="57" height="57" fill="none" stroke="${c}" stroke-width=".9"/>
    <rect x="6.5" y="6.5" width="51" height="51" fill="none" stroke="${c}" stroke-width=".5" stroke-dasharray="1.2 2"/>
    ${curl(8, 8, 1, 1)}${curl(56, 8, -1, 1)}${curl(8, 56, 1, -1)}${curl(56, 56, -1, -1)}
    <circle cx="32" cy="3.5" r="1.6" fill="${c}"/><circle cx="32" cy="60.5" r="1.6" fill="${c}"/>
    <circle cx="3.5" cy="32" r="1.6" fill="${c}"/><circle cx="60.5" cy="32" r="1.6" fill="${c}"/>`, 'preserveAspectRatio="none"');
}

// ── arcade cells (tab bar, stage arcade) ───────────────────────────────────────────────────────────────────────────

const ARCH = 'M4 100V46C4 25 27 10 50 3C73 10 96 25 96 46V100';
const SPANDREL = 'M0 0H100V100H96V46C96 25 73 10 50 3C27 10 4 25 4 46V100H0Z';

function archCell(kind: 'plain' | 'gilt' | 'hover'): string {
  const ns = 'vector-effect="non-scaling-stroke"';
  if (kind === 'gilt') {
    return svg(100, 100, `
      <path d="${SPANDREL}" fill="${P.gold}"/>
      <path d="M0 0H100" stroke="${P.goldLight}" stroke-width="2" ${ns}/>
      <path d="${ARCH}" fill="none" stroke="${P.ink}" stroke-width="2.5" ${ns}/>
      <path d="${ARCH}" fill="none" stroke="${P.goldLight}" stroke-width="1" ${ns} transform="translate(50 52) scale(.9 .94) translate(-50 -50)"/>
      <circle cx="12" cy="9" r="2.6" fill="${P.vermilion}" stroke="${P.ink}" stroke-width=".8" ${ns}/>
      <circle cx="88" cy="9" r="2.6" fill="${P.vermilion}" stroke="${P.ink}" stroke-width=".8" ${ns}/>`, 'preserveAspectRatio="none"');
  }
  const line = kind === 'hover' ? P.goldLight : P.gold;
  return svg(100, 100, `
    <path d="${SPANDREL}" fill="${P.lapis}" opacity=".85"/>
    <path d="M0 0H100" stroke="${P.shellGold}" stroke-width="1" ${ns}/>
    <path d="${ARCH}" fill="none" stroke="${line}" stroke-width="${kind === 'hover' ? 2 : 1.5}" ${ns}/>
    <circle cx="12" cy="9" r="2.2" fill="${P.shellGold}" ${ns}/><circle cx="88" cy="9" r="2.2" fill="${P.shellGold}" ${ns}/>`, 'preserveAspectRatio="none"');
}

/** The pointed-arch clip outline for painted tiles (k 0.35), as an SVG ring stroked along the arch (react ring). */
export const TILE_ARCH_PATH = 'M2 98V35C2 18 24 6 50 2C76 6 98 18 98 35V98Z';

// ── small marks ────────────────────────────────────────────────────────────────────────────────────────────────────

const lozenge = svg(10, 10, `<path d="M5 .9L9.1 5L5 9.1L.9 5Z" fill="${P.gold}" stroke="${P.ink}" stroke-width="1"/>`);
const deckle = svg(48, 12, `<path d="M0 12V5.2C3 3.6 5.6 6.8 9 5.4C12.4 4 13.6 2.6 17.2 3.6C21 4.6 22.6 6.4 26.4 5.2C30 4 31.4 2.8 35 3.8C38.4 4.8 40.8 6.6 44.2 5.2C46 4.5 47 4.3 48 5.2V12Z" fill="#000"/>`, 'preserveAspectRatio="none"');
const quill = svg(40, 6, `<path d="M1.5 3.6C9 1.2 27 .9 38.6 2.4C39.4 2.5 39.4 3.3 38.6 3.4C27 4.8 10 5.6 1.5 3.6Z" fill="${P.ink}"/>`);
const scrollEnd = svg(10, 40, `
  <path d="M10 1.2C4.6 1.2 1.2 3.4 1.2 6.4V33.6C1.2 36.6 4.6 38.8 10 38.8" fill="${P.vellumAged}" stroke="${P.ink}" stroke-width="1" vector-effect="non-scaling-stroke"/>
  <path d="M10 4.2C6.4 4.2 4.4 5.4 4.4 7.2C4.4 8.8 6.4 9.6 8 9.2M10 35.8C6.4 35.8 4.4 34.6 4.4 32.8" fill="none" stroke="${P.ink}" stroke-width=".8" vector-effect="non-scaling-stroke"/>`, 'preserveAspectRatio="none"');
const manicule = svg(28, 18, `
  <path d="M1.5 4.6H7.4V15.4H1.5Z" fill="${P.vellumAged}" stroke="${P.ink}" stroke-width="1"/>
  <path d="M3 4.6V15.4M5 4.6V15.4" stroke="${P.ink}" stroke-width=".5"/>
  <path d="M7.4 6.4C9.4 4.8 12 4.6 14.2 5.4H25.6C27.2 5.4 27.2 7.9 25.6 7.9H16.4C17.6 8.6 17.4 10.3 16.2 10.6C17.4 11.2 17.2 12.9 15.9 13.2C16.8 13.9 16.2 15.5 14.8 15.5H10.2C8.8 15.5 8 14.8 7.4 14Z"
    fill="${P.vellumLight}" stroke="${P.ink}" stroke-width="1" stroke-linejoin="round"/>
  <path d="M16.4 7.9H13.6M16.2 10.6H13M15.9 13.2H12.8" stroke="${P.ink}" stroke-width=".7" stroke-linecap="round"/>`);

/** The 16-lobe wax-seal outline (normalised 0..100), slightly irregular like poured wax. */
export function waxPath(lobes = 16, wobble = 0.018): string {
  const pts: string[] = [];
  const n = lobes * 8;
  for (let i = 0; i <= n; i++) {
    const t = (i / n) * Math.PI * 2;
    const r = 46.5 + 2.6 * Math.cos(lobes * t) + 100 * wobble * Math.sin(3 * t + 0.7) * 0.5;
    pts.push(`${(50 + r * Math.cos(t)).toFixed(2)} ${(50 + r * Math.sin(t)).toFixed(2)}`);
  }
  return `M${pts.join('L')}Z`;
}
const wax = svg(100, 100, `<path d="${waxPath()}" fill="#000"/>`);
/** Emboss over a wax disc: the pressed ring and a light catch (flat shapes, no gradient). */
const waxEmboss = svg(100, 100, `
  <circle cx="50" cy="50" r="33" fill="none" stroke="${P.rubric}" stroke-width="4"/>
  <circle cx="50" cy="50" r="30.5" fill="none" stroke="${P.minium}" stroke-width="1.2" opacity=".55"/>
  <path d="M22 34C26 24 35 17.5 46 16.5" fill="none" stroke="${P.leadWhite}" stroke-width="3" stroke-linecap="round" opacity=".45"/>
  <path d="${waxPath()}" fill="none" stroke="${P.bole}" stroke-width="2" opacity=".7"/>`);

const pearls = svg(8, 8, `<rect width="8" height="8" fill="${P.vermilion}"/><circle cx="4" cy="4" r="2.3" fill="${P.leadWhite}" stroke="${P.ink}" stroke-width=".6"/>`);
const diaper = svg(14, 14, `<path d="M7 0L14 7L7 14L0 7Z" fill="none" stroke="${P.ink}" stroke-width=".7" opacity=".14"/><circle cx="7" cy="7" r=".9" fill="${P.ink}" opacity=".12"/>`);
const hatch = svg(6, 6, `<path d="M-1 1L1 -1M0 6L6 0M5 7L7 5" stroke="${P.ink}" stroke-width=".7" opacity=".16"/>`);

/** The "used" stamp: a red wax seal with a cross, stamped over a spent tile. */
const usedSeal = svg(100, 100, `
  <path d="${waxPath(14, 0.03)}" fill="${P.vermilion}" stroke="${P.ink}" stroke-width="3"/>
  <circle cx="50" cy="50" r="30" fill="none" stroke="${P.rubric}" stroke-width="5"/>
  <path d="M36 36L64 64M64 36L36 64" stroke="${P.leadWhite}" stroke-width="7" stroke-linecap="round" opacity=".9"/>`);
const hourglass = svg(24, 24, `<path d="M6 3H18M6 21H18M7 3C7 9 11 10 11 12C11 14 7 15 7 21M17 3C17 9 13 10 13 12C13 14 17 15 17 21" fill="none" stroke="${P.ink}" stroke-width="1.8" stroke-linecap="round"/><path d="M9 19C10 16.5 14 16.5 15 19Z" fill="${P.gold}" stroke="${P.ink}" stroke-width="1"/>`);
const crossedQuill = svg(24, 24, `<path d="M4 20L19 5C20 4 21.5 4.5 20.5 6L6 21Z" fill="${P.vellumLight}" stroke="${P.ink}" stroke-width="1.4"/><path d="M5 5L19 19" stroke="${P.rubric}" stroke-width="2.6" stroke-linecap="round"/>`);
/** Lock badge for locked tiles / tabs (shell glyph look). */
const lockMark = svg(24, 24, `<rect x="5" y="10.5" width="14" height="10" rx="1.5" fill="${P.vellumLight}" stroke="${P.ink}" stroke-width="1.8"/><path d="M8.2 10.5V7.8C8.2 5.6 9.9 4 12 4C14.1 4 15.8 5.6 15.8 7.8V10.5" fill="none" stroke="${P.ink}" stroke-width="1.8"/>`);
/** Fleur-de-lis emboss for the ruler thumb and the seal toggle. */
const FLEUR = 'M50 18C44 26 43 34 47 42C41 38 33 38 29 44C26 49 29 55 35 54C32 50 35 46 40 48C44 50 46 54 46 58H40V63H46C45 70 42 74 38 77H62C58 74 55 70 54 63H60V58H54C54 54 56 50 60 48C65 46 68 50 65 54C71 55 74 49 71 44C67 38 59 38 53 42C57 34 56 26 50 18Z';
const sealThumb = svg(56, 56, `
  <g transform="scale(.56)"><path d="${waxPath()}" fill="${P.vermilion}" stroke="${P.ink}" stroke-width="3.5"/>
  <circle cx="50" cy="50" r="31" fill="none" stroke="${P.rubric}" stroke-width="4"/>
  <path d="${FLEUR}" fill="${P.rubric}" stroke="${P.bole}" stroke-width="1.5" transform="translate(50 50) scale(.62) translate(-50 -48)"/>
  <path d="M24 34C28 25 36 19.5 46 18.6" fill="none" stroke="${P.leadWhite}" stroke-width="4" stroke-linecap="round" opacity=".5"/></g>`);
const PAW = 'M50 54C40 54 33 62 33 70C33 77 40 79 50 77C60 79 67 77 67 70C67 62 60 54 50 54ZM34 50C30 50 27 45 28 40C29 35 33 33 36 35C39 37 40 42 38 46C37 49 36 50 34 50ZM66 50C70 50 73 45 72 40C71 35 67 33 64 35C61 37 60 42 62 46C63 49 64 50 66 50ZM44 44C40 44 38 39 38 34C38 29 41 26 44 26C47 26 50 30 49 35C49 40 47 44 44 44ZM56 44C60 44 62 39 62 34C62 29 59 26 56 26C53 26 50 30 51 35C51 40 53 44 56 44Z';
const pawSeal = svg(100, 100, `
  <path d="${waxPath()}" fill="${P.vermilion}" stroke="${P.ink}" stroke-width="3"/>
  <circle cx="50" cy="50" r="33" fill="none" stroke="${P.rubric}" stroke-width="4"/>
  <path d="${PAW}" fill="${P.rubric}" stroke="${P.bole}" stroke-width="1.6" transform="translate(50 52) scale(.78) translate(-50 -52)"/>
  <path d="M22 34C26 24 35 17.5 46 16.5" fill="none" stroke="${P.leadWhite}" stroke-width="3" stroke-linecap="round" opacity=".45"/>`);
/** A gold cartouche behind crit numbers (art §8.6). */
const cartouche = svg(80, 44, `<path d="M8 4H72C76 10 76 34 72 40H8C4 34 4 10 8 4Z" fill="${P.gold}" stroke="${P.ink}" stroke-width="2"/><path d="M12 8H68C71 14 71 30 68 36H12C9 30 9 14 12 8Z" fill="none" stroke="${P.goldLight}" stroke-width="1.2"/>`, 'preserveAspectRatio="none"');
/** A quatrefoil mask (the omen medallion keystone, art §8.2b): four lobes around a square core. */
const quatrefoil = svg(100, 100, `<g fill="#000"><circle cx="50" cy="27" r="25"/><circle cx="50" cy="73" r="25"/><circle cx="27" cy="50" r="25"/><circle cx="73" cy="50" r="25"/><rect x="27" y="27" width="46" height="46"/></g>`);
/** The ruled ink line + lozenge divider (art §9.2 rubric line). */
const ruleLine = svg(200, 10, `<path d="M0 3.5H200" stroke="${P.vermilion}" stroke-width="1"/><path d="M0 6.5H200" stroke="${P.ink}" stroke-width="1"/>`, 'preserveAspectRatio="none"');

/** Every ornament as `[cssVar, value]` (value = a CSS `url(...)`). */
export function ornamentVars(night = false): Record<string, string> {
  return {
    '--orn-penwork-edge-red': enc(penworkEdge(P.vermilion)),
    '--orn-penwork-edge-blue': enc(penworkEdge(P.lapisLight)),
    '--orn-penwork-edge-gold': enc(penworkEdge(P.goldLight)),
    '--orn-corner-red': enc(corner(P.vermilion, P.lapisLight)),
    '--orn-corner-blue': enc(corner(P.lapisLight, P.vermilion)),
    '--orn-penwork-red': enc(dropcapPenwork(night ? P.goldLight : P.vermilion)),
    '--orn-penwork-blue': enc(dropcapPenwork(P.lapisLight)),
    '--orn-arch-cell': enc(archCell('plain')),
    '--orn-arch-cell-gilt': enc(archCell('gilt')),
    '--orn-arch-cell-hover': enc(archCell('hover')),
    '--orn-lozenge': enc(lozenge),
    '--orn-deckle': enc(deckle),
    '--orn-quill-stroke': enc(quill),
    '--orn-scroll-end': enc(scrollEnd),
    '--orn-manicule': enc(manicule),
    '--orn-wax': enc(wax),
    '--orn-wax-emboss': enc(waxEmboss),
    '--orn-pearls': enc(pearls),
    '--orn-diaper': enc(diaper),
    '--orn-hatch': enc(hatch),
    '--orn-used-seal': enc(usedSeal),
    '--orn-hourglass': enc(hourglass),
    '--orn-crossed-quill': enc(crossedQuill),
    '--orn-lock': enc(lockMark),
    '--orn-seal-thumb': enc(sealThumb),
    '--orn-paw-seal': enc(pawSeal),
    '--orn-cartouche': enc(cartouche),
    '--orn-rule': enc(ruleLine),
    '--orn-quatrefoil': enc(quatrefoil),
  };
}

/** Writes the ornament custom properties on `root` (idempotent). */
export function applyOrnaments(root: HTMLElement = document.documentElement): void {
  for (const [k, v] of Object.entries(ornamentVars(root.dataset.theme === 'night'))) root.style.setProperty(k, v);
}
